import type { ReactNode } from "react";
import { regionOptions, styleOptions, yearOptions } from "@/lib/catalog";

type Props = {
  selectedYears: string[];
  selectedRegions: string[];
  selectedStyles: string[];
  onToggle: (group: "years" | "regions" | "styles", value: string) => void;
  onReset: () => void;
};

function Chip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer whitespace-nowrap rounded-md border px-2.5 py-1.5 text-[12px] transition-colors font-label ${
        active
          ? "border-accent-500/80 bg-accent-500/15 text-accent-300"
          : "border-background-300/70 text-foreground-400 hover:border-accent-500/50 hover:text-foreground-100"
      }`}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-t border-background-300/50 py-4 first:border-t-0 first:pt-0">
      <h3 className="text-[10px] tracking-widest2 text-foreground-500 font-label">{title}</h3>
      <div className="mt-3 flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function FilterPanel({ selectedYears, selectedRegions, selectedStyles, onToggle, onReset }: Props) {
  const activeCount = selectedYears.length + selectedRegions.length + selectedStyles.length;

  return (
    <div className="rounded-lg border border-background-300/60 bg-background-100 p-4">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 font-heading text-[13px] font-semibold text-foreground-100">
          <i className="ri-filter-3-line text-accent-500" />
          筛选
          {activeCount > 0 && (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] text-foreground-50 font-label">
              {activeCount}
            </span>
          )}
        </p>
        <button
          type="button"
          onClick={onReset}
          disabled={activeCount === 0}
          className="cursor-pointer text-[11px] text-foreground-500 transition-colors hover:text-accent-400 disabled:cursor-not-allowed disabled:opacity-40 font-label"
        >
          重置
        </button>
      </div>

      <div className="mt-4">
        <Group title="年份">
          {yearOptions.map((year) => (
            <Chip
              key={year}
              label={year}
              active={selectedYears.includes(year)}
              onClick={() => onToggle("years", year)}
            />
          ))}
        </Group>
        <Group title="地区">
          {regionOptions.map((region) => (
            <Chip
              key={region}
              label={region}
              active={selectedRegions.includes(region)}
              onClick={() => onToggle("regions", region)}
            />
          ))}
        </Group>
        <Group title="风格">
          {styleOptions.map((style) => (
            <Chip
              key={style.slug}
              label={style.name}
              active={selectedStyles.includes(style.slug)}
              onClick={() => onToggle("styles", style.slug)}
            />
          ))}
        </Group>
      </div>
    </div>
  );
}